import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Dropdown } from 'react-bootstrap';

const CHART_HEIGHT = 380;

const chartTypes = [
    { id: 'line', name: 'Line Chart', icon: 'bi-graph-up' },
    { id: 'area', name: 'Area Chart', icon: 'bi-area-chart' },
    { id: 'bar', name: 'Bar Chart', icon: 'bi-bar-chart' },
];

function AppointmentsRatioChart({ data = [] }) {
    const [timeRange, setTimeRange] = useState('30');
    const [chartType, setChartType] = useState('line');
    const [filteredData, setFilteredData] = useState(() => (data || []).slice(-30));

    useEffect(() => {
        const days = timeRange === 'All' ? (data || []).length : parseInt(timeRange, 10);
        setFilteredData((data || []).slice(-days));
    }, [data, timeRange]);

    const handleTimeRangeChange = (days) => {
        setTimeRange(days);
        const sliceDays = days === 'All' ? (data || []).length : parseInt(days, 10);
        setFilteredData((data || []).slice(-sliceDays));
    };

    const baseOptions = {
        chart: {
            type: 'line',
            toolbar: { show: false },
            zoom: { enabled: false },
            parentHeightOffset: 0,
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800,
            },
        },
        colors: ['#6366f1', '#10b981'],
        dataLabels: { enabled: false },
        stroke: {
            curve: 'smooth',
            width: 2,
        },
        xaxis: {
            type: 'category',
            categories: filteredData.map((row) => row.day),
            labels: {
                rotate: -45,
                style: { fontSize: '12px', colors: '#6c757d' },
            },
            axisBorder: { show: true, color: '#e9ecef' },
            axisTicks: { show: true, color: '#e9ecef' },
        },
        yaxis: {
            title: {
                text: 'Appointments',
                style: { fontWeight: 'normal', color: '#6c757d', fontSize: '12px' },
            },
            min: 0,
            forceNiceScale: true,
            labels: {
                formatter: (val) => Math.round(val).toLocaleString(),
            },
        },
        grid: {
            borderColor: '#f8f9fa',
            strokeDashArray: 3,
        },
        tooltip: {
            enabled: true,
            shared: true,
            intersect: false,
            y: { formatter: (val) => `${Number(val || 0).toLocaleString()} appointments` },
        },
        legend: {
            position: 'top',
            horizontalAlign: 'right',
            markers: { width: 12, height: 12, radius: 6 },
        },
        responsive: [
            {
                breakpoint: 600,
                options: {
                    xaxis: {
                        labels: {
                            rotate: -45,
                            style: { fontSize: '10px' },
                        },
                    },
                },
            },
        ],
    };

    const getChartOptions = () => {
        switch (chartType) {
            case 'area':
                return {
                    ...baseOptions,
                    chart: { ...baseOptions.chart, type: 'area', stacked: false },
                    fill: {
                        type: 'solid',
                        opacity: 0.55,
                    },
                };
            case 'line':
                return {
                    ...baseOptions,
                    chart: { ...baseOptions.chart, type: 'line' },
                    markers: { size: 4 },
                    stroke: { ...baseOptions.stroke, width: 3 },
                };
            case 'bar':
                return {
                    ...baseOptions,
                    chart: { ...baseOptions.chart, type: 'bar', stacked: false },
                    plotOptions: {
                        bar: {
                            horizontal: false,
                            borderRadius: 6,
                            borderRadiusApplication: 'end',
                            columnWidth: '60%',
                        },
                    },
                    fill: { type: 'solid', opacity: 0.82 },
                    stroke: {
                        ...baseOptions.stroke,
                        show: true,
                        width: 2,
                        colors: ['#6366f1', '#10b981'],
                    },
                };
            default:
                return baseOptions;
        }
    };

    const series = [
        {
            name: 'Total',
            data: filteredData.map((row) => row.total),
        },
        {
            name: 'Confirmed',
            data: filteredData.map((row) => row.confirmed),
        },
    ];

    return (
        <div className="card shadow-sm border-0 sms-dashboard-chart-card">
            <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h5 className="card-title mb-0 fw-normal">
                    <i className="bi bi-graph-up text-primary me-2" />
                    Daily Appointment Activity
                </h5>

                <div className="d-flex flex-wrap gap-2">
                    <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                            <i className={`bi ${chartTypes.find((t) => t.id === chartType)?.icon} me-1`} />
                            {chartTypes.find((t) => t.id === chartType)?.name}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {chartTypes.map((type) => (
                                <Dropdown.Item
                                    key={type.id}
                                    active={chartType === type.id}
                                    onClick={() => setChartType(type.id)}
                                >
                                    <i className={`bi ${type.icon} me-2`} />
                                    {type.name}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>

                    <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                            Last {timeRange} days
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleTimeRangeChange('7')}>Last 7 days</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleTimeRangeChange('14')}>Last 14 days</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleTimeRangeChange('30')}>Last 30 days</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleTimeRangeChange('All')}>All time</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>

            <div className="card-body p-3 sms-dashboard-chart">
                <ReactApexChart
                    options={getChartOptions()}
                    series={series}
                    type={chartType === 'bar' ? 'bar' : chartType}
                    height={CHART_HEIGHT}
                    width="100%"
                />
            </div>

            <div className="card-footer bg-light border-top-0 small text-muted">
                <i className="bi bi-info-circle me-1" />
                Hover over chart elements to see daily total and confirmed counts
            </div>
        </div>
    );
}

export default AppointmentsRatioChart;
